import React, { useState, useEffect, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Popup,
  useMap,
  Polygon,
  Circle,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Button, Select, Spin, Typography, Card, Tag, Tooltip, Grid, Divider } from "antd";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  ApiOutlined,
  AimOutlined,
  RadarChartOutlined,
} from "@ant-design/icons";
import { LocationIcons } from "./LocationIcons";
import L from "leaflet";
import buildOSRMUrl from "./OSRM";
import {
  getGeofencesList,
  startLiveTrackingSession,
  stopLiveTrackingSession,
} from "../../mqtt/location";

const { Text, Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

function interpolatePosition(start, end, t) {
  return [
    start[0] + (end[0] - start[0]) * t,
    start[1] + (end[1] - start[1]) * t,
  ];
}

function PanToVehicleSmart({ position }) {
  const map = useMap();
  useEffect(() => {
    if (!position) return;
    const bounds = map.getBounds();
    const paddedBounds = bounds.pad(-0.08);
    if (!paddedBounds.contains(L.latLng(position))) {
      map.panTo(position, { animate: true, duration: 1 });
    }
  }, [position, map]);
  return null;
}

const LIVE_TRACKING_CSS = `
@keyframes lt-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(22,163,74,0.45); }
  50%       { box-shadow: 0 0 0 7px rgba(22,163,74,0); }
}
@keyframes lt-pulse-red {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
  50%       { box-shadow: 0 0 0 7px rgba(239,68,68,0); }
}
@keyframes lt-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

const StatChip = ({ icon, label, value, hex = "#2563eb", bg = "#eff6ff", border = "#bfdbfe" }) => (
  <div
    style={{
      display: "flex", alignItems: "center", gap: 7,
      padding: "6px 12px", borderRadius: 9,
      background: bg, border: `1px solid ${border}`,
      animation: "lt-fade-in 0.35s ease both",
    }}
  >
    <span style={{ fontSize: 13, color: hex }}>{icon}</span>
    <div>
      <div style={{ fontSize: 9, fontWeight: 700, color: hex, opacity: 0.72, textTransform: "uppercase", letterSpacing: "0.07em", lineHeight: 1 }}>{label}</div>
      <div style={{ fontSize: 11, fontWeight: 700, color: hex, fontFamily: "monospace", marginTop: 2 }}>{value}</div>
    </div>
  </div>
);

const RouteProgress = ({ current, total, playing }) => {
  const pct = total > 1 ? Math.round((current / (total - 1)) * 100) : 0;
  const color = pct === 100 ? "#16a34a" : playing ? "#2563eb" : "#94a3b8";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ flex: 1, height: 5, borderRadius: 10, background: "#e2e8f0", overflow: "hidden" }}>
        <div
          style={{
            height: "100%", width: `${pct}%`, borderRadius: 10,
            background: color,
            transition: "width 0.6s ease, background 0.3s ease",
          }}
        />
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color, minWidth: 34, textAlign: "right" }}>
        {pct}%
      </span>
    </div>
  );
};

const LiveTracking = ({
  plots,
  vehicle,
  liveTrackingSessionState,
  sendLocationToServer,
  stopSendingLocationToServer,
  setVehiclePositionInParent,
}) => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const { serialNo, vehicleKey } = vehicle || {};
  const [selectedTripIdx, setSelectedTripIdx] = useState(0);
  const [osrmRoute, setOsrmRoute] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [animatedPos, setAnimatedPos] = useState(null);
  const [sendingLocation, setSendingLocation] = useState(false);
  const intervalRef = useRef(null);
  const animationRef = useRef(null);
  const [previousLiveTrackingSessionId, setPreviousLiveTrackingSessionId] = useState(null);
  const [geofenceLoading, setGeofenceLoading] = useState(false);
  const [activeGeofences, setActiveGeofences] = useState([]);
  const [ongoingSession, setOngoingSession] = useState(false);

  // inject CSS once
  useEffect(() => {
    let el = document.getElementById("lt-animations");
    if (!el) {
      el = document.createElement("style");
      el.id = "lt-animations";
      document.head.appendChild(el);
    }
    el.textContent = LIVE_TRACKING_CSS;
    return () => { document.getElementById("lt-animations")?.remove(); };
  }, []);

  const fetchGeofences = async () => {
    try {
      setGeofenceLoading(true);
      const response = await getGeofencesList(vehicleKey);
      setActiveGeofences(response.data.filter((fence) => fence.status === "active"));
    } catch (err) {
      console.error("Error fetching geofences:", err);
    } finally {
      setGeofenceLoading(false);
    }
  };

  const { sessionId: liveTrackingSessionId, action: liveTrackingSessionAction } =
    liveTrackingSessionState || {};

  const selectedTrip = plots[selectedTripIdx];
  const path = [
    selectedTrip.startLocation.coordinates,
    ...selectedTrip.places.map((p) => p.location.coordinates),
    selectedTrip.endLocation.coordinates,
  ];

  useEffect(() => {
    async function fetchRoute() {
      setLoading(true);
      setOsrmRoute([]);
      try {
        const url = buildOSRMUrl(path);
        const res = await fetch(url);
        const data = await res.json();
        if (data.routes?.[0]?.geometry?.coordinates) {
          setOsrmRoute(data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]));
        }
      } catch {
        setOsrmRoute([]);
      }
      setLoading(false);
    }
    fetchRoute();
    setCurrentIdx(0);
    setPlaying(false);
    setAnimatedPos(null);
    // eslint-disable-next-line
  }, [selectedTripIdx]);

  useEffect(() => {
    if (!playing || osrmRoute.length < 2) return;
    const startIdx = currentIdx;
    const endIdx = Math.min(currentIdx + 1, osrmRoute.length - 1);
    if (startIdx === endIdx) return;
    setAnimating(true);
    const start = osrmRoute[startIdx];
    const end = osrmRoute[endIdx];
    let startTime = null;
    function animate(ts) {
      if (!startTime) startTime = ts;
      const t = Math.min((ts - startTime) / 1000, 1);
      setAnimatedPos(interpolatePosition(start, end, t));
      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setAnimatedPos(null);
        setCurrentIdx((idx) => {
          if (idx < osrmRoute.length - 1) return idx + 1;
          setPlaying(false);
          return idx;
        });
        setAnimating(false);
      }
    }
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      setAnimating(false);
      setAnimatedPos(null);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // eslint-disable-next-line
  }, [playing, currentIdx, osrmRoute.length]);

  useEffect(() => {
    if (playing && !animating && osrmRoute.length > 1 && currentIdx < osrmRoute.length - 1) {
      setTimeout(() => { setAnimating(true); }, 0);
    }
  }, [playing, animating, currentIdx, osrmRoute.length]);

  const handleSelectTrip = (idx) => { setSelectedTripIdx(idx); setCurrentIdx(0); setPlaying(false); setAnimatedPos(null); };
  const handlePlay = () => { if (!playing) setPlaying(true); };
  const handlePause = () => setPlaying(false);
  const handleReset = () => { setCurrentIdx(0); setPlaying(false); setAnimatedPos(null); };

  const vehiclePos =
    animatedPos ||
    (osrmRoute.length > 0 ? osrmRoute[currentIdx] : path[Math.min(currentIdx, path.length - 1)]);

  const totalSteps = osrmRoute.length > 0 ? osrmRoute.length : path.length;
  const isAtEnd = currentIdx >= totalSteps - 1;

  const startNewLiveTrackingSession = async () => {
    if (!vehicleKey) return;
    try {
      setLoading(true);
      const response = await startLiveTrackingSession({ vehicleKey, serialNo });
      if (response?.data?.sessionId) {
        setOngoingSession(true);
        setPreviousLiveTrackingSessionId(response.data.sessionId);
      }
    } catch (err) {
      console.error("Failed to start live tracking session:", err);
    } finally {
      setLoading(false);
    }
  };

  const stopOngoingSession = async () => {
    const sessionId = previousLiveTrackingSessionId || liveTrackingSessionId;
    if (!sessionId) return;
    try {
      setLoading(true);
      await stopLiveTrackingSession({ vehicleKey, serialNo, sessionId });
      setOngoingSession(false);
      setPreviousLiveTrackingSessionId(null);
    } catch (err) {
      console.error("Failed to stop live tracking session:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (liveTrackingSessionAction === "ON" && liveTrackingSessionId) {
      setSendingLocation(true);
    } else {
      stopSendingLocationToServer();
      setSendingLocation(false);
    }
  }, [liveTrackingSessionAction, liveTrackingSessionId, stopSendingLocationToServer]);

  useEffect(() => {
    if (sendingLocation) setVehiclePositionInParent(vehiclePos);
  }, [vehiclePos, setVehiclePositionInParent, sendingLocation]);

  const getCenterPointForGeofenceCircle = (center) => [center.lat, center.lng];
  const getFencePointsForGeofence = (coordinates) => coordinates.map((p) => [p.lat, p.lng]);

  const mapHeight = isMobile ? 300 : 480;

  // path options
  const activeRoutePath = { color: "#2563eb", weight: 4, opacity: 0.85 };
  const fallbackRoutePath = { color: "#94a3b8", weight: 3, opacity: 0.6, dashArray: "8 6" };
  const geofenceActivePath = { color: "#2563eb", fillColor: "#2563eb", fillOpacity: 0.08, weight: 2 };

  return (
    <>
      <div
        style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          marginBottom: 16, flexWrap: "wrap", justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 38, height: 38, borderRadius: 10,
              background: sendingLocation ? "#f0fdf4" : "#f8fafc",
              border: `1px solid ${sendingLocation ? "#86efac" : "#e2e8f0"}`,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <AimOutlined style={{ fontSize: 18, color: sendingLocation ? "#16a34a" : "#94a3b8" }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0 }}>Live Tracking</Title>
            <Text style={{ fontSize: 12, color: "#94a3b8" }}>
              Simulate vehicle movement along a route and stream location updates
            </Text>
          </div>
        </div>

        {/* tracking status pill */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {liveTrackingSessionId && (
            <div
              style={{
                padding: "4px 12px", borderRadius: 20,
                background: "#f8fafc", border: "1px solid #e2e8f0",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <ApiOutlined style={{ fontSize: 11, color: "#94a3b8" }} />
              <span style={{ fontSize: 10, fontFamily: "monospace", color: "#64748b" }}>
                {liveTrackingSessionId}
              </span>
            </div>
          )}
          <div
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "5px 14px", borderRadius: 20,
              background: sendingLocation ? "#f0fdf4" : "#fef2f2",
              border: `1px solid ${sendingLocation ? "#86efac" : "#fca5a5"}`,
            }}
          >
            <div
              style={{
                width: 8, height: 8, borderRadius: "50%",
                background: sendingLocation ? "#16a34a" : "#ef4444",
                animation: sendingLocation ? "lt-pulse 1.8s infinite" : "lt-pulse-red 2s infinite",
              }}
            />
            <span
              style={{
                fontSize: 11, fontWeight: 700,
                color: sendingLocation ? "#16a34a" : "#ef4444",
              }}
            >
              {sendingLocation ? "Tracking ON" : "Tracking OFF"}
            </span>
          </div>
        </div>
      </div>

      <Card
        style={{ borderRadius: 12, border: "1px solid #e2e8f0", marginBottom: 14 }}
        styles={{ body: { padding: "14px 18px" } }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>

          {/* left: route selector + playback */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {/* route selector */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Text style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>Route</Text>
              <Select
                value={selectedTripIdx}
                onChange={handleSelectTrip}
                style={{ width: 190 }}
                size="small"
              >
                {plots.map((plot, idx) => (
                  <Option key={plot.tripId} value={idx}>
                    {plot.tripId}
                  </Option>
                ))}
              </Select>
            </div>

            <Divider type="vertical" style={{ height: 24, margin: 0 }} />

            {/* playback buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {!playing ? (
                <Tooltip title="Play">
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlayCircleOutlined />}
                    onClick={handlePlay}
                    disabled={isAtEnd}
                    style={{ borderRadius: 8, fontWeight: 600 }}
                  >
                    Play
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Pause">
                  <Button
                    size="small"
                    icon={<PauseCircleOutlined />}
                    onClick={handlePause}
                    style={{ borderRadius: 8, fontWeight: 600 }}
                  >
                    Pause
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Reset to start">
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  disabled={currentIdx === 0 && !playing}
                  style={{ borderRadius: 8 }}
                >
                  Reset
                </Button>
              </Tooltip>
            </div>

            {/* playback status chip */}
            <Tag
              style={{
                margin: 0, borderRadius: 20, fontSize: 10, fontWeight: 700,
                background: isAtEnd ? "#f0fdf4" : playing ? "#eff6ff" : "#f8fafc",
                border: `1px solid ${isAtEnd ? "#86efac" : playing ? "#bfdbfe" : "#e2e8f0"}`,
                color: isAtEnd ? "#16a34a" : playing ? "#2563eb" : "#94a3b8",
              }}
            >
              {isAtEnd ? "● Destination reached" : playing ? "● Moving" : "● Stopped"}
            </Tag>
          </div>

          {/* right: session + geofences */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div id="start-stop-live-tracking-session-controls">
              {ongoingSession ? (
                <Button
                  disabled
                  danger
                  size="small"
                  style={{ borderRadius: 8 }}
                  onClick={stopOngoingSession}
                >
                  Stop Session
                </Button>
              ) : (
                <Button
                  disabled
                  size="small"
                  style={{ borderRadius: 8 }}
                  onClick={startNewLiveTrackingSession}
                >
                  Start Session
                </Button>
              )}
            </div>

            <Button
              size="small"
              icon={<EnvironmentOutlined />}
              loading={geofenceLoading}
              onClick={fetchGeofences}
              style={{ borderRadius: 8 }}
            >
              {geofenceLoading
                ? "Loading…"
                : `Geofences${activeGeofences.length > 0 ? ` (${activeGeofences.length})` : ""}`}
            </Button>
          </div>
        </div>

        {/* progress bar */}
        <div style={{ marginTop: 12 }}>
          <RouteProgress current={currentIdx} total={totalSteps} playing={playing} />
        </div>
      </Card>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
        <StatChip
          icon={<AimOutlined />}
          label="Latitude"
          value={vehiclePos[0].toFixed(5)}
          hex="#7c3aed"
          bg="#f5f3ff"
          border="#c4b5fd"
        />
        <StatChip
          icon={<AimOutlined />}
          label="Longitude"
          value={vehiclePos[1].toFixed(5)}
          hex="#7c3aed"
          bg="#f5f3ff"
          border="#c4b5fd"
        />
        <StatChip
          icon={<RadarChartOutlined />}
          label="Step"
          value={`${currentIdx + 1} / ${totalSteps}`}
          hex="#0891b2"
          bg="#ecfeff"
          border="#a5f3fc"
        />
        {activeGeofences.length > 0 && (
          <StatChip
            icon={<EnvironmentOutlined />}
            label="Active Geofences"
            value={`${activeGeofences.length} zone${activeGeofences.length !== 1 ? "s" : ""}`}
            hex="#2563eb"
            bg="#eff6ff"
            border="#bfdbfe"
          />
        )}
      </div>

      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span>Route Map</span>
            <Tag
              style={{
                margin: 0, borderRadius: 20, fontSize: 10, fontWeight: 600,
                background: osrmRoute.length > 0 ? "#eff6ff" : "#f8fafc",
                color: osrmRoute.length > 0 ? "#2563eb" : "#94a3b8",
                border: `1px solid ${osrmRoute.length > 0 ? "#bfdbfe" : "#e2e8f0"}`,
              }}
            >
              {osrmRoute.length > 0 ? `OSRM · ${osrmRoute.length} pts` : "Fallback path"}
            </Tag>
            {activeGeofences.length > 0 && (
              <Tag
                style={{
                  margin: 0, borderRadius: 20, fontSize: 10, fontWeight: 600,
                  background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe",
                }}
              >
                {activeGeofences.length} geofence{activeGeofences.length !== 1 ? "s" : ""}
              </Tag>
            )}
          </div>
        }
        style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ borderRadius: "0 0 10px 10px", overflow: "hidden" }}>
          {loading ? (
            <div
              style={{
                height: mapHeight, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 12,
                background: "#f8fafc",
              }}
            >
              <Spin size="large" />
              <Text style={{ fontSize: 12, color: "#94a3b8" }}>Calculating route…</Text>
            </div>
          ) : (
            <MapContainer
              center={vehiclePos}
              zoom={13}
              style={{ height: mapHeight, width: "100%" }}
            >
              <PanToVehicleSmart position={vehiclePos} />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />

              {/* Route lines */}
              {osrmRoute.length > 0 ? (
                <Polyline positions={osrmRoute} pathOptions={activeRoutePath} />
              ) : (
                <Polyline positions={path} pathOptions={fallbackRoutePath} />
              )}

              {/* Start marker */}
              <Marker position={selectedTrip.startLocation.coordinates} icon={LocationIcons.startIcon}>
                <Popup>
                  <strong>Start</strong><br />
                  {selectedTrip.startLocation.coordinates[0].toFixed(5)},{" "}
                  {selectedTrip.startLocation.coordinates[1].toFixed(5)}
                </Popup>
              </Marker>

              {/* Place markers */}
              {selectedTrip.places.map((place) => (
                <Marker key={place.placeId} position={place.location.coordinates} icon={LocationIcons.placeIcon}>
                  <Popup>
                    <strong>{place.placeId}</strong><br />
                    {place.time}
                  </Popup>
                </Marker>
              ))}

              {/* End marker */}
              <Marker position={selectedTrip.endLocation.coordinates} icon={LocationIcons.endIcon}>
                <Popup>
                  <strong>Destination</strong><br />
                  {selectedTrip.endLocation.coordinates[0].toFixed(5)},{" "}
                  {selectedTrip.endLocation.coordinates[1].toFixed(5)}
                </Popup>
              </Marker>

              {/* Vehicle marker */}
              <Marker
                position={vehiclePos}
                icon={sendingLocation ? LocationIcons.vehicleRippleEffect : LocationIcons.vehicleRedIcon}
              >
                <Popup>
                  <strong>Vehicle</strong><br />
                  {vehiclePos[0].toFixed(5)}, {vehiclePos[1].toFixed(5)}<br />
                  {sendingLocation ? "📡 Broadcasting location" : "⏸ Not broadcasting"}
                </Popup>
              </Marker>

              {/* Active geofences */}
              {activeGeofences.map((fence, idx) =>
                fence.type === "polygon" ? (
                  <Polygon
                    key={idx}
                    positions={getFencePointsForGeofence(fence.coordinates)}
                    pathOptions={geofenceActivePath}
                  >
                    <Popup><strong>{fence.name || `Geofence #${idx + 1}`}</strong><br />Polygon zone</Popup>
                  </Polygon>
                ) : (
                  <Circle
                    key={idx}
                    center={getCenterPointForGeofenceCircle(fence.center)}
                    radius={fence.radius}
                    pathOptions={geofenceActivePath}
                  >
                    <Popup>
                      <strong>{fence.name || `Geofence #${idx + 1}`}</strong><br />
                      Radius: {fence.radius}m
                    </Popup>
                  </Circle>
                )
              )}
            </MapContainer>
          )}
        </div>
      </Card>
    </>
  );
};

export default LiveTracking;
